import { ApplicantStatus } from '@components/templates/employer/applicant-list/functions';
import {
  McApplicantCardData,
  McApplicantsPage,
  McFlowBuckets,
  McFlowTotals,
  McProfileIdentifierType,
  McProfileSource,
  McUnifiedProfile,
  McUnifiedProfileResult,
} from '@components/templates/employer/applicant-list/flow-mc/types';

const mapProfileSourceToInterestType = (profileSource?: McProfileSource | null): ApiSchema<'Interest'>['type'] | undefined => {
  if (profileSource === 'applicant') return 'application';
  if (profileSource === 'saved_profile') return 'saved';
  if (profileSource === 'matching_candidate') return 'suggestion';
  if (profileSource === 'recommended_candidate') return 'recommendation';
  return undefined;
};

const MC_PROFILE_IDENTIFIER_USER_PREFIX = 'user:';
const MC_PROFILE_IDENTIFIER_PHONE_PREFIX = 'phone:';

const normalizeMcSlug = (slug?: string | null) => {
  const normalizedSlug = slug?.trim();
  return normalizedSlug || undefined;
};

const normalizeMcProfileIdentifier = (profileIdentifier?: string | null) => {
  const normalizedIdentifier = profileIdentifier?.trim();
  return normalizedIdentifier || undefined;
};

export const resolveMcProfileIdentifierType = (profileIdentifier?: string | null): McProfileIdentifierType => {
  const normalizedIdentifier = normalizeMcProfileIdentifier(profileIdentifier);

  if (!normalizedIdentifier) return 'other';
  if (normalizedIdentifier.includes(MC_PROFILE_IDENTIFIER_USER_PREFIX)) return 'user';
  if (normalizedIdentifier.includes(MC_PROFILE_IDENTIFIER_PHONE_PREFIX)) return 'phone';
  return 'other';
};

export const extractMcPhoneIdentifierValue = (profileIdentifier?: string | null) => {
  const normalizedIdentifier = normalizeMcProfileIdentifier(profileIdentifier);

  if (!normalizedIdentifier?.includes(MC_PROFILE_IDENTIFIER_PHONE_PREFIX)) return undefined;

  const phoneIdentifierValue = normalizedIdentifier.split(MC_PROFILE_IDENTIFIER_PHONE_PREFIX)[1]?.trim();
  return phoneIdentifierValue || undefined;
};

const normalizeMcSummary = (summary?: string | null) => {
  const normalizedSummary = summary?.trim();
  return normalizedSummary || undefined;
};

const hasMcBulkActionProfileIdentifier = (profileIdentifier?: string | null) => (
  resolveMcProfileIdentifierType(profileIdentifier) === 'user'
);

export const extractMcBulkActionSlug = (profileIdentifier?: string | null, slug?: string | null) => {
  if (!hasMcBulkActionProfileIdentifier(profileIdentifier)) return undefined;

  return normalizeMcSlug(slug);
};

export const normalizeMcBulkActionSlugs = (
  selectedApplicants: string[],
  items: Pick<McApplicantCardData, 'mcBulkActionSlug'>[] = [],
) => {
  const validSlugs = new Set(
    items
      .map((item) => item.mcBulkActionSlug)
      .filter((slug): slug is string => Boolean(slug)),
  );

  return Array.from(
    new Set(
      selectedApplicants.filter((selectedApplicant) => validSlugs.has(selectedApplicant)),
    ),
  );
};

export const mapUnifiedProfileToMcApplicant = (
  profile: McUnifiedProfile,
): McApplicantCardData | null => {
  const profileIdentifier = normalizeMcProfileIdentifier(profile.profileIdentifier);

  if (!profileIdentifier) return null;

  const profileIdentifierType = resolveMcProfileIdentifierType(profileIdentifier);
  const profileSource = profile.profileSource ?? null;
  const interestType = mapProfileSourceToInterestType(profileSource);
  const normalizedStatus = typeof profile.status === 'number' ? profile.status : ApplicantStatus.NEW;
  const profileSlug = normalizeMcSlug(profile.slug);
  const profilePhoneIdentifierValue = extractMcPhoneIdentifierValue(profileIdentifier);
  const profileSummary = normalizeMcSummary(profile.summary);
  const profileEvaluationStatus = typeof profile.evaluationStatus === 'number' ? profile.evaluationStatus : undefined;
  const profileFinalScore = typeof profile.finalScore === 'number' ? profile.finalScore : undefined;

  return {
    id: profileIdentifier,
    slug: profileSlug,
    mcProfileIdentifier: profileIdentifier,
    mcProfileIdentifierType: profileIdentifierType,
    mcPhoneIdentifierValue: profilePhoneIdentifierValue ?? null,
    mcBulkActionSlug: extractMcBulkActionSlug(profileIdentifier, profileSlug) ?? null,
    mcProfileSource: profileSource,
    mcReferenceDate: profile.referenceDate ?? null,
    mcProcessingStatus: profile.processingStatus ?? null,
    mcEvaluationStatus: profileEvaluationStatus ?? null,
    mcSummary: profileSummary ?? null,
    mcFinalScore: profileFinalScore ?? null,
    mcIdentifier: typeof profile.identifier === 'number' ? profile.identifier : null,
    online: false,
    hasUnreadMessage: false,
    locked: false,
    imgUrl: profile.imgUrl ?? undefined,
    location: profile.location ?? undefined,
    privateInformation: {
      name: profile.privateInformation?.name ?? undefined,
      phone: profile.privateInformation?.phone ?? undefined,
      email: profile.privateInformation?.email ?? undefined,
      videoUrl: undefined,
      interviewVideoStatus: 'missing',
      hasInterviewVideo: false,
      hasJobInterviewVideo: false,
    },
    leadExperience: profile.experience
      ? {
        title: profile.experience.title ?? undefined,
        company: profile.experience.company ?? undefined,
        period: profile.experience.period ?? undefined,
      }
      : undefined,
    interest: interestType
      ? {
        type: interestType,
        value: profile.referenceDate ?? undefined,
      }
      : undefined,
    application: {
      statusId: normalizedStatus,
      isNew: !profile.viewedAt,
    },
    hasVideos: profile.processingStatus === 'INTERVIEW_COMPLETED',
  };
};

export const mapUnifiedProfileResultToMcApplicantsPage = (
  result?: McUnifiedProfileResult,
): McApplicantsPage | undefined => {
  if (!result) return undefined;

  return {
    items: result.items?.map(mapUnifiedProfileToMcApplicant).filter(Boolean) as McApplicantCardData[] | undefined,
    total: result.total,
    pages: result.pages,
    page: result.page,
  };
};

const isApplicantColumnStatus = (status?: number) => status === ApplicantStatus.NEW || status === ApplicantStatus.VIEWED;
const isInterviewColumnStatus = (status?: number) => (
  status === ApplicantStatus.INTERVIEW
  || status === ApplicantStatus.OFFER
  || status === ApplicantStatus.HIRED
);

export const bucketMcApplicantsByStatus = (items?: McApplicantCardData[]): McFlowBuckets => {
  const buckets: McFlowBuckets = {
    rejected: [],
    allApplicants: [],
    shortlist: [],
    interview: [],
  };

  (items ?? []).forEach((item) => {
    const status = item.application?.statusId;

    if (status === ApplicantStatus.REJECTED) {
      buckets.rejected.push(item);
      return;
    }

    if (status === ApplicantStatus.SHORTLIST) {
      buckets.shortlist.push(item);
      return;
    }

    if (isInterviewColumnStatus(status)) {
      buckets.interview.push(item);
      return;
    }

    if (isApplicantColumnStatus(status) || typeof status !== 'number') {
      buckets.allApplicants.push(item);
    }
  });

  return buckets;
};

export const getMcFlowTotals = (buckets: McFlowBuckets): McFlowTotals => ({
  rejected: buckets.rejected.length,
  allApplicants: buckets.allApplicants.length,
  shortlist: buckets.shortlist.length,
  interview: buckets.interview.length,
  total: buckets.rejected.length + buckets.allApplicants.length + buckets.shortlist.length + buckets.interview.length,
});
